// FE-SCRM-060 — <IncludeArchivedToggle> + ?archived=on URL codec.
//
// Example:
//
//     // Inside any list panel
//     <IncludeArchivedToggle />
//     // The list query reads `archived` off ?archived=on so the
//     // checkbox stays in sync with shareable / bookmarked URLs.
//
// Mounts on every Phase-7b list (Prospects, Contacts, Opportunities).
// When ``true``, the list surfaces ARCHIVED rows with a muted style
// + an "Unarchive" row action; when ``false`` (the default), only
// active rows are queried.

import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";


const URL_KEY = "archived";
const ON_VALUE = "on";


export function readIncludeArchivedFromUrl(
  params: URLSearchParams,
): boolean {
  return params.get(URL_KEY) === ON_VALUE;
}


export function writeIncludeArchivedToParams(
  include: boolean, base: URLSearchParams,
): URLSearchParams {
  const out = new URLSearchParams(base);
  out.delete(URL_KEY);
  if (include) out.set(URL_KEY, ON_VALUE);
  return out;
}


export interface IncludeArchivedToggleProps {
  label?: string;
  className?: string;
}


export function IncludeArchivedToggle({
  label = "Include archived",
  className,
}: IncludeArchivedToggleProps): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const checked = readIncludeArchivedFromUrl(searchParams);

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchParams(
        writeIncludeArchivedToParams(e.target.checked, searchParams),
        { replace: false },
      );
    },
    [searchParams, setSearchParams],
  );

  return (
    <label
      className={
        "inline-flex items-center gap-2 text-sm text-gray-700 "
        + (className ?? "")
      }
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className={
          "h-4 w-4 rounded border-gray-300 text-indigo-600 "
          + "focus:ring-indigo-500"
        }
      />
      <span>{label}</span>
    </label>
  );
}
